// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { authService } from '../api/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialAuthChecked, setInitialAuthChecked] = useState(false);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) {
        setUser(session?.user ?? null);
        setLoading(false);
        setInitialAuthChecked(true);
      }
    };
    init();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        // The 'INITIAL_SESSION' event can be handled here as well if needed,
        // but getSession() on init usually covers this.
        // On SIGNED_IN, Supabase provides the session which includes the user.
        setUser(session?.user ?? null);
        if (event === 'SIGNED_OUT') {
          // No need to setLoading(true) here as it's a state change, not an async op start
          navigate('/login', { replace: true });
        }
        // After the first auth event or initial load, set loading to false
        // This helps prevent premature redirects if an async auth operation is in progress
        if (initialAuthChecked || event) {
            setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, [navigate, initialAuthChecked]);

  const signInWithOtp = async (email) => {
    setLoading(true);
    const result = await authService.signInWithOtp(email);
    // setLoading(false) will be handled by onAuthStateChange or if error
    if (!result.success) setLoading(false);
    return result;
  };

  const loginWithEmailPassword = async (email, password) => {
    setLoading(true);
    const result = await authService.signInWithEmailPassword(email, password);
    // onAuthStateChange will set user and setLoading(false) on success
    if (!result.success) setLoading(false);
    return result;
  };

  const signUpWithEmailPassword = async (email, password) => {
    setLoading(true);
    const result = await authService.signUpWithEmailPassword(email, password);
    // onAuthStateChange will set user and setLoading(false) if signUp auto-logins
    // or if email confirmation is off. Otherwise, user stays null.
    // If email confirmation is required, user won't be set until they confirm.
    setLoading(false); // Set loading false regardless, as signup attempt is complete.
    return result;
  };

  const logout = async () => {
    setLoading(true);
    await authService.logout();
    // onAuthStateChange will handle redirect and setUser(null)
    // setLoading(false) will be handled by onAuthStateChange
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading, // loading now represents initial load AND ongoing auth operations
        initialAuthChecked, // useful to know if the first session check has completed
        isAuthenticated: !!user,
        signInWithOtp,
        loginWithEmailPassword,
        signUpWithEmailPassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);